import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { slotKey } from '../utils/date';

// Carica solo gli appuntamenti con data >= fromDateString ("YYYY-MM-DD").
// Le date sono stringhe ISO, quindi il confronto lessicografico coincide con
// quello cronologico. Usato all'avvio per caricare solo mese corrente + futuro
// (tutta la disponibilità che serve al paziente) invece dell'intera collezione,
// rendendo l'apertura molto più veloce. Lo storico completo (per l'admin) viene
// caricato a parte con loadAppointments().
export const loadAppointmentsFrom = async (fromDateString) => {
  try {
    const q = query(collection(db, 'appointments'), where('date', '>=', fromDateString));
    const querySnapshot = await getDocs(q);
    const appointments = [];
    querySnapshot.forEach((docSnap) => {
      appointments.push({ id: docSnap.id, ...docSnap.data() });
    });
    console.log('Caricati da Firebase (dal', fromDateString + '):', appointments.length, 'appuntamenti');
    return appointments;
  } catch (error) {
    console.error('Errore caricamento appuntamenti (parziale):', error);
    return [];
  }
};

// Salva un appuntamento.
//
// Per le prenotazioni pubbliche (enforceSlotUnique = true) usa un ID documento
// deterministico ricavato da data+ora + una scrittura in SOLA CREAZIONE. La
// protezione anti-overbooking è delegata alle regole di sicurezza Firestore:
// su /appointments l'update è negato (allow update: if false), quindi se lo
// slot è già occupato questa setDoc diventa un update e viene rifiutata dal
// server con "permission-denied" — che qui interpretiamo come slot già preso.
//
// NB: non si usa runTransaction perché sul piano gratuito Firestore le
// transaction falliscono con RESOURCE_EXHAUSTED quando la quota giornaliera è
// esaurita; letture e scritture semplici invece funzionano. Questo approccio è
// anche più leggero (nessuna lettura extra) e resta gratuito.
//
// IMPORTANTE: ogni progetto creato da questo template DEVE pubblicare le regole
// in firestore.rules (allow update: if false su /appointments), altrimenti la
// protezione anti-overbooking non è attiva.
//
// Per gli eventi privati/admin (enforceSlotUnique = false) mantiene addDoc, così
// l'admin resta libero di inserire più eventi sullo stesso orario (es. weekend).
export const saveAppointment = async (appointmentData, { enforceSlotUnique = false } = {}) => {
  if (enforceSlotUnique) {
    const id = slotKey(appointmentData.date, appointmentData.time);
    const slotRef = doc(db, 'appointments', id);
    try {
      await setDoc(slotRef, {
        ...appointmentData,
        createdAt: Timestamp.now()
      });
      console.log('Appuntamento salvato in Firebase:', id);
      return { success: true, id };
    } catch (error) {
      if (error.code === 'permission-denied') {
        console.warn('Slot già prenotato (update negato dalle regole):', id);
        return { success: false, reason: 'slot_taken' };
      }
      console.error('Errore salvataggio:', error);
      return { success: false, error };
    }
  }

  try {
    const docRef = await addDoc(collection(db, 'appointments'), {
      ...appointmentData,
      createdAt: Timestamp.now()
    });
    console.log('Appuntamento salvato in Firebase:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Errore salvataggio:', error);
    return { success: false, error };
  }
};

export const loadAppointments = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'appointments'));
    const appointments = [];
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    console.log('Caricati da Firebase:', appointments.length, 'appuntamenti');
    return appointments;
  } catch (error) {
    console.error('Errore caricamento:', error);
    return [];
  }
};

export const deleteAppointment = async (id) => {
  try {
    await deleteDoc(doc(db, 'appointments', id));
    console.log('Appuntamento eliminato da Firebase:', id);
    return true;
  } catch (error) {
    console.error('Errore eliminazione:', error);
    return false;
  }
};

export const saveSettings = async (settings) => {
  try {
    await setDoc(doc(db, 'settings', 'main'), settings);
    console.log('Impostazioni salvate in Firebase');
    return true;
  } catch (error) {
    console.error('Errore salvataggio impostazioni:', error);
    return false;
  }
};

export const loadSettings = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'settings'));
    if (!querySnapshot.empty) {
      const settings = querySnapshot.docs[0].data();
      console.log('Impostazioni caricate da Firebase');
      return settings;
    }
    return null;
  } catch (error) {
    console.error('Errore caricamento impostazioni:', error);
    return null;
  }
};

export const cleanOldAppointments = async () => {
  try {
    const thirteenMonthsAgo = new Date();
    thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13);
    
    const querySnapshot = await getDocs(collection(db, 'appointments'));
    const deletePromises = [];
    
    querySnapshot.forEach((doc) => {
      const appointment = doc.data();
      const aptDate = new Date(appointment.date);
      
      if (aptDate < thirteenMonthsAgo) {
        deletePromises.push(deleteDoc(doc.ref));
      }
    });
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log(`Eliminati ${deletePromises.length} appuntamenti oltre i 13 mesi`);
    }
    
    return deletePromises.length;
  } catch (error) {
    console.error('Errore pulizia automatica:', error);
    return 0;
  }
};

export const saveVaccination = async (vaccinationData) => {
  try {
    const docRef = await addDoc(collection(db, 'vaccinations'), {
      ...vaccinationData,
      createdAt: Timestamp.now()
    });
    console.log('Vaccinazione salvata in Firebase:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Errore salvataggio vaccinazione:', error);
    return { success: false, error };
  }
};

export const loadVaccinations = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'vaccinations'));
    const vaccinations = [];
    querySnapshot.forEach((doc) => {
      vaccinations.push({ id: doc.id, ...doc.data() });
    });
    console.log('Caricate da Firebase:', vaccinations.length, 'richieste vaccinazioni');
    return vaccinations;
  } catch (error) {
    console.error('Errore caricamento vaccinazioni:', error);
    return [];
  }
};

export const deleteVaccination = async (id) => {
  try {
    await deleteDoc(doc(db, 'vaccinations', id));
    console.log('Vaccinazione eliminata da Firebase:', id);
    return true;
  } catch (error) {
    console.error('Errore eliminazione vaccinazione:', error);
    return false;
  }
};

export const saveBlockedDate = async (dateString, reason = '') => {
  try {
    await setDoc(doc(db, 'blockedDates', dateString), {
      date: dateString,
      reason: reason,
      createdAt: Timestamp.now()
    });
    console.log('Data bloccata salvata:', dateString);
    return { success: true, id: dateString };
  } catch (error) {
    console.error('Errore nel salvataggio data bloccata:', error);
    return { success: false, error: error.message };
  }
};

export const removeBlockedDate = async (dateString) => {
  try {
    await deleteDoc(doc(db, 'blockedDates', dateString));
    console.log('Data sbloccata:', dateString);
    return true;
  } catch (error) {
    console.error('Errore nella rimozione data bloccata:', error);
    return false;
  }
};

export const loadBlockedDates = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'blockedDates'));
    const blockedDates = [];
    
    querySnapshot.forEach((doc) => {
      blockedDates.push({ 
        id: doc.id, 
        ...doc.data() 
      });
    });
    
    console.log('Date bloccate caricate:', blockedDates.length);
    return blockedDates;
  } catch (error) {
    console.error('Errore nel caricamento date bloccate:', error);
    return [];
  }
};